import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useCallback } from 'react';
import { gql, request } from 'graphql-request';
import { useQuery } from 'react-query';

import type { IQueryKeyFactory } from '~/helpers/types';
import { getAppSettings } from '~/lib/appSettings';

export enum IFontAwesomeIconFamily {
  Classic = 'CLASSIC',
  Duotone = 'DUOTONE',
  Sharp = 'SHARP',
}

export enum IFontAwesomeIconStyle {
  Brands = 'BRANDS',
  Duotone = 'DUOTONE',
  Light = 'LIGHT',
  Regular = 'REGULAR',
  Solid = 'SOLID',
  Thin = 'THIN',
}

export type IUseFontAwesomeSearchIconsQueryValues = {
  accessToken: string;
  query: string;
  family: IFontAwesomeIconFamily;
  style: IFontAwesomeIconStyle;
  limit?: number;
};

export type IFontAwesomeSearchIconFamilyStyleResponseBody = {
  /**
   * A family name, like “classic", “duotone", or “sharp".
   */
  family?: string;

  /**
   * A style name, like “solid", “regular", “light", “thin", or “brands".
   */
  style?: string;

  /**
   * A prefix, like “fas" (for the Classic Solid familyStyle), or “fasl" (for
   * the Sharp Light familyStyle). A prefix is a single term that distinctly
   * identifies a familyStyle.
   */
  prefix?: string;
};

export type IFontAwesomeSearchIconSvgResponseBody = {
  /**
   * The familyStyle of the SVG.
   */
  familyStyle?: IFontAwesomeSearchIconFamilyStyleResponseBody;

  /**
   * The height of the SVG viewBox.
   */
  height?: number;

  /**
   * The width of the SVG viewBox.
   */
  width?: number;

  /**
   * The svg formatted as an html `<svg>` element.
   */
  html?: string;

  /**
   * The svg formatted as an IconDefinition, usable in the Font Awesome
   * JavaScript API.
   * @see https://docs.fontawesome.com/apis/javascript/icon-library#using-the-library
   */
  iconDefinition?: object;

  /**
   * Each item is a value for a `d` attribute in an svg `<path>` element. A
   * monotone icon has exactly one path, the primary path. A duotone icon always
   * has two paths. When duotone, the first list element corresponds to the
   * icon’s secondary path, and the second list element corresponds to the
   * icon’s primary path. Heads up! This might seem counterintuitive: the first
   * path (at list index 0) is the secondary layer for a duotone icon. Either
   * path may be an empty string, indicating no path for that layer. Some
   * duotone icons have only a primary path, while other duotone icons have only
   * a secondary path.
   */
  pathData?: Array<string>;
};

export type IFontAwesomeSearchIconResponseBody = {
  /**
   * The identifying name of an icon, like pizza-slice or bicycle.
   */
  id?: string;

  /**
   * Usually, a more human readable representation of this icon. For example,
   * the icon with id "coffee-pot" has a label of "Coffee Pot".
   */
  label?: string;

  /**
   * Unicode by which this icon can be identified—just the hex digits as a
   * string. For example, the value of this field for the coffee icon is "f0f4".
   */
  unicode?: string;

  /**
   * SVGs for this icon.
   */
  svgs?: Array<IFontAwesomeSearchIconSvgResponseBody>;
};

export type IFontAwesomeSearchIconsResponseBody = {
  search: Array<IFontAwesomeSearchIconResponseBody>;
};

const iconsQueryDocument = gql`
  query searchIcons(
    $query: String!
    $family: String!
    $style: String!
    $limit: Int
  ) {
    search(version: "latest", query: $query, first: $limit) {
      id
      label
      unicode
      svgs(filter: { familyStyles: { family: $family, style: $style } }) {
        familyStyle {
          prefix
        }
        width
        height
        pathData
        html
      }
    }
  }
`;

export const fontAwesomeSearchIconsQueryKeyFactory: IQueryKeyFactory<
  IUseFontAwesomeSearchIconsQueryValues
> = (values?) =>
  values &&
  ([
    'fontAwesomeSearchIcons',
    {
      query: values.query,
      family: values.family,
      style: values.style,
      limit: values.limit,
    },
  ] as const);

export const useFontAwesomeSearchIconsQuery = (
  values?: IUseFontAwesomeSearchIconsQueryValues,
  options?: UseQueryOptions<IFontAwesomeSearchIconsResponseBody>,
): UseQueryResult<IFontAwesomeSearchIconsResponseBody> => {
  const appSettings = getAppSettings();

  const fetchIcons = useCallback(
    (
      query: IUseFontAwesomeSearchIconsQueryValues,
    ): Promise<IFontAwesomeSearchIconsResponseBody> =>
      request(appSettings.fontAwesome.apiUrl, iconsQueryDocument, query, {
        Authorization: `Bearer ${values?.accessToken}`,
      }),
    [appSettings.fontAwesome.apiUrl, values?.accessToken],
  );

  const query = useQuery<IFontAwesomeSearchIconsResponseBody>({
    queryKey: fontAwesomeSearchIconsQueryKeyFactory(values),
    queryFn: values
      ? () => (values.query ? fetchIcons(values) : { search: [] })
      : undefined,
    enabled: !!values,
    ...options,
  });

  return query;
};
